/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from 'react';
import type {
  ReactNode,
  ReactElement,
  Ref,
} from 'react';

import type {
  GroupBase,
} from 'react-select';

import { SelectBase } from '../SelectBase';

import {
  creatableTexts,
} from '../ReactSelectExtended/texts';
import {
  useStyles,
} from '../ReactSelectExtended/styles';
import { selectComponentsSync } from '../ReactSelectExtended/components';
import type {
  OptionTypeBase,
  SelectBaseProps,
} from '../ReactSelectExtended/types';

import {
  ReactSelectCreatableExtended,
} from '../ReactSelectExtended/ReactSelectExtended';
import type {
  ReactSelectCreatableExtendedProps,
} from '../ReactSelectExtended/ReactSelectExtended';

export type CreatableProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
> =
  & ReactSelectCreatableExtendedProps<OptionType, IsMulti, Group>
  & Omit<SelectBaseProps<OptionType, Group, IsMulti>, 'defaultComponents' | 'children'>;

function CreatableComponent<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(
  props: CreatableProps<OptionType, Group, IsMulti>,
  ref: Ref<HTMLDivElement>,
): ReactElement {
  const {
    hasError = false,
    hasWarning = false,
    isClearable = true,
    isMulti = false,
    small = false,
    renderModal = undefined,
    selectRef = null,
    ...restProps
  } = props;

  const styles = useStyles(small);

  return (
    <SelectBase
      defaultComponents={selectComponentsSync}
      {...props}
      ref={ref}
    >
      {({
        getOptionLabel,
        getOptionValue,
        components,
      }): ReactNode => (
        <ReactSelectCreatableExtended
          {...creatableTexts}
          isMulti={isMulti}
          hasError={hasError}
          hasWarning={hasWarning}
          small={small}
          closeMenuOnSelect={!isMulti}
          isArrowHidden={Boolean(renderModal)}
          isClearable={isClearable}
          components={components}
          styles={styles}
          {...restProps}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          ref={selectRef}
        />
      )}
    </SelectBase>
  );
}

export const Creatable = forwardRef(CreatableComponent) as <
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(
  props: CreatableProps<OptionType, Group, IsMulti> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
